// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import setSelectedVideoIndex from "@gcg/App/Handlers/Pages/ProductDetails/SetSelectedVideoIndex";
import { CustomAppState } from "@gcg/App/Store/Reducers";
import { getSelectedVideoIndex } from "@gcg/App/Store/Selectors/ProductDetailsSelectors";
import ProductImageCarousel, { ProductImageCarouselStyles } from "@gcg/Overrides/Components/ProductImageCarousel";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage/LazyImage";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState, ownProps: HasProduct) => ({
    selectedImageIndex: state.pages.productDetails.selectedImageIndex,
    selectedVideoIndex: getSelectedVideoIndex(state, ownProps.product),
});

const mapDispatchToProps = {
    setSelectedImageIndex,
    setSelectedVideoIndex,
};

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductDetailsImageCarouselStyles {
    imageCarousel?: ProductImageCarouselStyles;

    /**
     * @deprecated Use imageCarousel styles instead.
     */
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    image?: LazyImageProps;
    selectedImage?: LazyImageProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonProps;
}

export const productDetailsImageCarouselStyles: ProductDetailsImageCarouselStyles = {};

const styles = productDetailsImageCarouselStyles;

const ProductDetailsImageCarousel: React.FC<Props> = ({
    product,
    selectedImageIndex,
    selectedVideoIndex,
    setSelectedImageIndex,
    setSelectedVideoIndex,
}) => {
    const selectImageHandler = (index: number) => {
        setSelectedVideoIndex({ index: -1, product });
        setSelectedImageIndex({ index });
    };

    const selectVideoHandler = (index: number) => {
        setSelectedVideoIndex({ index, product });
    };

    return (
        <ProductImageCarousel
            selectedIndex={selectedImageIndex}
            selectedVideoIndex={selectedVideoIndex}
            onSelectImage={selectImageHandler}
            onSelectVideo={selectVideoHandler}
            extendedStyles={styles.imageCarousel}
        />
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsImageCarousel)),
    definition: {
        displayName: "Image Carousel",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
